import { WeekCalendarCellServant } from '../../servants/week_calendar/week_calendar_servant';
import { TimeOccurrenceServant } from '../../servants/time/time_occurrence_servant';
import { TimeOccurrenceDataSource } from '../../datastore/time/time_datasource';
import { IRawTimeOccurrenceDatatype, ITimeOccurrenceDatatype } from '../../datastore/time/data_types';
import { IWeekCell } from './data_types';
import { TimeOccurrenceEngine } from '../../engines/time/time_engine';
import { OrderedTimeQueryModel } from '../../query_models/ordered_time_query_model';
import { TimeStartEndIdentifierEnum } from '../../engines/time/time_types';
import { IValidator } from '../../validation/contracts';
import { IDayOfTheWeekValue, IDayOfTheWeekEnum } from 'utility-js-time';
import * as _4 from 'lodash4';

interface IWeekCalendarQueries {
  times: OrderedTimeQueryModel;
}

interface IVisualization {
  hourDivisions: number;
  hourPercentageHeight: number;
  hourRange: Array<IHourDivision>;
  getOnlineClasses: Function;
  getOfflineClasses: Function;
}
interface IHourDivision {
  humanFormat: string;
  val: number;
}
interface ISpecification {
  hourStep: number;
  hourBegin: number;
  hourEnd: number;
  colorIndex: number;
}

const MODES = {
  SWAP: 'swap'
};

export class Drag {
  isPainting: boolean = false;
  isCreating: boolean = false;
  isMoving: boolean = false;
  isResizing: boolean = false;
  isDragging: boolean = false;
  isDraggable: boolean = false;
  hoveredCell: any = undefined;
  extendUp: boolean = false;
  extendDown: boolean = false;
  clickedCell: any = undefined;
  hoveredTimeOccurrenceTrackingId: any = undefined;
  originalTimeOccurrence: any = undefined;
  currentTimeOccurrence: any = undefined;
  constructor(
    private startTimeDatasourceTransactionCommand: any,
    private ngWeekCalendarCellServant: any,
    private addBuiltTimeCommand: any,
    private updateTimeCommand: any,
    private timeLockValidatorFactory: any,
    private timeEndValidatorFactory: any,
    private timeStartValidatorFactory: any,
    private timeOverlapValidatorFactory: any,
    private endTimeDatasourceTransactionCommand: any,
    private ngTimeOccurrenceEngine: any,
    private deleteTimeCommand: any,
    private copyTimePropCommand: any,
    private copyTimeOccurrenceToCellCommand: any,
    private ngTimeOccurrenceDataSource: any
  ) {}
  startPainting($event: Event): void {
    this.startTimeDatasourceTransactionCommand();

    if (this.isPainting && this.currentTimeOccurrence) {
      return;
    }
    this.isPainting = true;

    this.clickedCell = this.ngWeekCalendarCellServant.createCellFromEvent($event);

    if (this.ngWeekCalendarCellServant.isCellOpen(this.clickedCell, this.ngTimeOccurrenceDataSource.store)) {
      this.isCreating = true;
      let newTimeOccurrence: ITimeOccurrenceDatatype = this.ngWeekCalendarCellServant.createTimeOccurrenceForCell(
        this.clickedCell
      );
      this.addBuiltTimeCommand(newTimeOccurrence).then((addedBuiltTime: ITimeOccurrenceDatatype) => {
        this.currentTimeOccurrence = this.originalTimeOccurrence = addedBuiltTime;
      });
    } else {
      this.isCreating = false;
      this.currentTimeOccurrence = this.originalTimeOccurrence =
        this.ngWeekCalendarCellServant.findTimeOccurrenceForCell(
          this.clickedCell,
          this.ngTimeOccurrenceDataSource.store
        );
      this.isMoving = !this.isResizing && !this.originalTimeOccurrence.isLocked;

      if (!this.isResizing) {
        // is there a better solution than calling startTransaction twice?
        const boundTimeLockValidator: IValidator = this.timeLockValidatorFactory(this.originalTimeOccurrence);
        this.startTimeDatasourceTransactionCommand([
          boundTimeLockValidator,
          this.timeEndValidatorFactory,
          this.timeStartValidatorFactory
        ]);

        this.updateTimeCommand(this.originalTimeOccurrence, { isPreview: true }).then(
          (builtTime: ITimeOccurrenceDatatype) => {
            this.originalTimeOccurrence = builtTime;
          }
        );
      }
    }
  }
  endPainting($event: Event): void {
    if (this.isCreating || this.isResizing || this.isMoving) {
      const boundOverlapValidator: IValidator = this.timeOverlapValidatorFactory(
        this.originalTimeOccurrence,
        this.currentTimeOccurrence
      );
      this.endTimeDatasourceTransactionCommand(boundOverlapValidator);
    }

    this.currentTimeOccurrence = undefined;
    this.originalTimeOccurrence = undefined;
    this.isPainting = false;
    this.isCreating = false;
    this.isMoving = false;
    this.clickedCell = undefined;
    this.extendUp = false;
    this.extendDown = false;
    this.isResizing = false;
    // this.isDraggable = false;
  }
  hoverCell($event: Event): void {
    // TechDebt: Why try catch?
    // TODO: Fix how the calendar grid is drawn so that the actual calendar workspace doesn't bleed to the right
    let hoveredCell: IWeekCell;

    try {
      hoveredCell = this.ngWeekCalendarCellServant.createCellFromEvent($event);
    } catch (err) {
      return;
    }

    if (_4.isEqual(this.clickedCell, hoveredCell)) {
      return;
    }

    let hoveredCellTimeOccurrence: ITimeOccurrenceDatatype =
      this.ngWeekCalendarCellServant.createTimeOccurrenceForCell(hoveredCell);

    let hoveredCellExistingTimeOccurrence: ITimeOccurrenceDatatype =
      this.ngWeekCalendarCellServant.findTimeOccurrenceForCell(hoveredCell, this.ngTimeOccurrenceDataSource.store);

    // find out if we're resizing or not:
    let hoveredOverStartOrEnd: any;

    if (hoveredCellExistingTimeOccurrence) {
      hoveredOverStartOrEnd = this.ngTimeOccurrenceEngine.getTimeStartOrEnd(
        hoveredCellTimeOccurrence,
        hoveredCellExistingTimeOccurrence
      );
      this.isDraggable = !hoveredCellExistingTimeOccurrence.isLocked;
      this.hoveredTimeOccurrenceTrackingId = hoveredCellExistingTimeOccurrence.trackingId;
    } else {
      this.isDraggable = false;
      this.hoveredTimeOccurrenceTrackingId = undefined;
    }

    if (
      hoveredOverStartOrEnd === TimeStartEndIdentifierEnum.Start &&
      !this.isResizing &&
      !this.isMoving &&
      !hoveredCellExistingTimeOccurrence.isLocked
    ) {
      this.extendUp = true;
      this.isResizing = true;
    } else if (
      hoveredOverStartOrEnd === TimeStartEndIdentifierEnum.End &&
      !this.isResizing &&
      !this.isMoving &&
      !hoveredCellExistingTimeOccurrence.isLocked
    ) {
      this.extendDown = true;
      this.isResizing = true;
    } else if (!hoveredOverStartOrEnd && !this.clickedCell) {
      this.extendUp = false;
      this.extendDown = false;
      this.isResizing = false;
    }

    if (this.isMoving) {
      this.isDraggable = true;

      let deletedTimeCopy = {};
      // ghost original time occurrence here
      // delete time that is being dragged
      if (this.currentTimeOccurrence !== this.originalTimeOccurrence) {
        deletedTimeCopy = _.clone(this.currentTimeOccurrence);
        this.deleteTimeCommand(this.currentTimeOccurrence);
      }
      // add new time occurrence with adjusted time here
      this.copyTimeOccurrenceToCellCommand(
        hoveredCell,
        this.currentTimeOccurrence,
        this.originalTimeOccurrence,
        deletedTimeCopy
      ).then((builtTime: ITimeOccurrenceDatatype) => {
        this.currentTimeOccurrence = builtTime;
      });
    }

    if (this.isCreating) {
      let propHash: Object = {};
      let property: string;

      if (this.ngTimeOccurrenceEngine.isTimeEarlier(this.originalTimeOccurrence, hoveredCellTimeOccurrence)) {
        property = 'to_tm';
      } else {
        property = 'from_tm';
      }

      this.copyTimePropCommand(propHash, property, hoveredCellTimeOccurrence, this.currentTimeOccurrence).then(
        (builtTime: ITimeOccurrenceDatatype) => {
          this.currentTimeOccurrence = builtTime;
        }
      );
    }

    if (this.isResizing && this.currentTimeOccurrence && !this.isMoving && !this.isCreating) {
      let propHash: Object = {};
      let property: string;

      property = !!this.extendUp ? 'from_tm' : 'to_tm';

      this.copyTimePropCommand(propHash, property, hoveredCellTimeOccurrence, this.currentTimeOccurrence).then(
        (builtTime: ITimeOccurrenceDatatype) => {
          this.currentTimeOccurrence = builtTime;
        }
      );
    }
  }
}

export class WeekCalendarViewController {
  inPrefMode: boolean;
  queries: IWeekCalendarQueries;
  specification: ISpecification;
  hourStep: string;
  hourBegin: string;
  hourEnd: string;
  visualization: IVisualization;
  drag: Drag;
  mode: string;
  __onMouseLeaveWindowBound = this.__onMouseLeaveWindow.bind(this);
  hourHeightPx: number = 29;

  constructor(
    private DayOfTheWeekEnum: IDayOfTheWeekEnum,
    private ngWeekCalendarCellServant: WeekCalendarCellServant,
    private ngTimeOccurrenceServant: TimeOccurrenceServant,
    private ngTimeOccurrenceEngine: TimeOccurrenceEngine,
    private ngTimeOccurrenceDataSource: TimeOccurrenceDataSource,
    private copyTimeOccurrenceToCellCommand: any,
    private deleteTimeCommand: any,
    private updateTimeCommand: any,
    private addBuiltTimeCommand: any,
    private copyTimePropCommand: any,
    private startTimeDatasourceTransactionCommand: any,
    private endTimeDatasourceTransactionCommand: any,
    private ngOrderedTimeQueryModel: OrderedTimeQueryModel,
    private timeOverlapValidatorFactory: any,
    private timeLockValidatorFactory: any,
    private timeEndValidatorFactory: any,
    private timeStartValidatorFactory: any
  ) {}
  initialize() {
    this.specification = {
      hourStep: parseInt(this.hourStep || '1', 10),
      hourBegin: parseInt(this.hourBegin || '8', 10),
      hourEnd: parseInt(this.hourEnd || '22', 10),
      colorIndex: 0
    };
    const hourDivisions = (this.specification.hourEnd - this.specification.hourBegin) / this.specification.hourStep + 1;
    this.visualization = {
      hourDivisions: hourDivisions,
      hourPercentageHeight: 100 / hourDivisions,
      hourRange: _4.times(hourDivisions, (i): IHourDivision => {
        return {
          humanFormat: moment()
            .hour(this.specification.hourBegin + i)
            .minute(0)
            .format('h a'),
          val: this.specification.hourBegin + i
        };
      }),
      getOnlineClasses: () => {
        return _4.filter(this.queries.times.model, (time: any): boolean => {
          return this.DayOfTheWeekEnum.isEqual(time.day, this.DayOfTheWeekEnum.ONLINE);
        });
      },
      getOfflineClasses: () => {
        const offlineClassTimes = _4.filter(this.queries.times.model, (time: any) => {
          return !this.DayOfTheWeekEnum.isEqual(time.day, this.DayOfTheWeekEnum.ONLINE);
        });
        _4.each(offlineClassTimes, (time) => {
          time.top = this.timeUnitTop(time);
          time.height = this.timeUnitHeight(time);
        });
        return offlineClassTimes;
      }
    };
  }

  __onMouseLeaveWindow($event: Event) {
    this.drag.endPainting($event);
  }

  createDrag() {
    this.drag = new Drag(
      this.startTimeDatasourceTransactionCommand,
      this.ngWeekCalendarCellServant,
      this.addBuiltTimeCommand,
      this.updateTimeCommand,
      this.timeLockValidatorFactory,
      this.timeEndValidatorFactory,
      this.timeStartValidatorFactory,
      this.timeOverlapValidatorFactory,
      this.endTimeDatasourceTransactionCommand,
      this.ngTimeOccurrenceEngine,
      this.deleteTimeCommand,
      this.copyTimePropCommand,
      this.copyTimeOccurrenceToCellCommand,
      this.ngTimeOccurrenceDataSource
    );
    window.removeEventListener('mouseup', this.__onMouseLeaveWindowBound);
    window.addEventListener('mouseup', this.__onMouseLeaveWindowBound);
  }
  alignTimesColumnLeft(alignTimes: string): boolean {
    return alignTimes === 'left';
  }
  $onInit() {
    this.inPrefMode = false;
    this.queries = {
      times: this.ngOrderedTimeQueryModel
    };
    this.createDrag();
  }

  $onDestroy() {
    window.removeEventListener('mouseup', this.__onMouseLeaveWindowBound);
  }

  $onChanges(changes: any) {
    if (!_4.isUndefined(changes.prefsModeActive)) {
      this.inPrefMode = changes.prefsModeActive.currentValue;
    }

    this.initialize();
  }

  getOnlineClassColSpan(includeWeekends: string): number {
    return includeWeekends === 'false' ? 5 : 7;
  }

  isDayOnWeekend(timeOccurrence: ITimeOccurrenceDatatype): boolean {
    return this.ngTimeOccurrenceServant.isTimeOccurrenceOnWeekend(timeOccurrence);
  }

  dimTile(timeOccurrence: ITimeOccurrenceDatatype): boolean {
    if (this.mode !== 'swap') {
      return false;
    }
    return !this.ngTimeOccurrenceServant.isTimeInSwap(timeOccurrence);
  }

  timeToPercentage(time: any): number {
    return this.visualization.hourPercentageHeight * time;
  }

  calculateSpatialTime(hour: any, minutes: any): number {
    return hour - this.specification.hourBegin + this.convertMinutesToHours(minutes);
  }

  convertMinutesToHours(minutes: any): number {
    return minutes / 60;
  }
  // FIXME: Style recalculation every digest cycle create expensive layout reflow.
  // FIXME: Set bounds for top and bottom. top is min of 0, and max of hourEnd - fromtime
  timeUnitTop(time: any): string {
    const hour = time.from.hour() - this.specification.hourBegin;
    const minutes = this.convertMinutesToHours(time.from.minutes());
    return this.timeToPercentage(hour + minutes) + '%';
  }

  timeUnitHeight(time: any): string {
    const to = this.calculateSpatialTime(time.to.hour(), time.to.minutes());
    const from = this.calculateSpatialTime(time.from.hour(), time.from.minutes());
    return this.timeToPercentage(to - from) + '%';
  }

  canDeleteTime(time: ITimeOccurrenceDatatype): boolean {
    return !time.isLocked && this.drag.isDraggable && time.trackingId === this.drag.hoveredTimeOccurrenceTrackingId;
  }

  deleteTime(time: ITimeOccurrenceDatatype): void {
    if (!time.isLocked) {
      this.startTimeDatasourceTransactionCommand();
      this.deleteTimeCommand(time);
      this.endTimeDatasourceTransactionCommand();
    }
  }
}

angular
  .module('ui-ng-calendar')
  .component('phxWeekCalendarView', {
    bindings: {
      enums: '<',
      includeWeekends: '@',
      microheaders: '@',
      hourStep: '@',
      hourBegin: '@',
      hourEnd: '@',
      hour: '@',
      prefsModeActive: '<',
      mode: '<'
    },
    templateUrl: 'ui-ng-calendar/src/views/week_calendar_view/week_calendar_view.html',
    controller: 'WeekCalendarViewController'
  })
  .controller('WeekCalendarViewController', WeekCalendarViewController);



// WEBPACK FOOTER //
// ./app/vendor/ui-ng-calendar/src/views/week_calendar_view/week_calendar_component.ts